/**
 * Pure wall-clock formatting in an explicit IANA zone (sites-timezone
 * follow-up). Admin surfaces display shift windows in the SITE's effective
 * timezone — not the viewer's device zone — so a Chicago supervisor reading a
 * Denver shift sees Denver times. DOM-free and Intl-only, so it is unit
 * testable without a browser.
 */

/**
 * The DEVICE-LOCAL calendar date as `YYYY-MM-DD` — "today" as the reader's own
 * clock names it.
 *
 * NOT `toISOString().slice(0, 10)`: that string is the UTC date, which for any
 * viewer west of UTC rolls over to "tomorrow" around the evening — a
 * requirement effective today refused as "in the past", a relationship ended
 * "today" that records tomorrow's date in the org calendar, an empty "today"
 * map query. Date-only columns (`effective_from`, the workforce-map day) are
 * calendar days, and the only calendar this client can honestly name without a
 * zone read is the viewer's own. Where an org zone IS available, callers use
 * `formatDayInZone`-family helpers instead.
 */
export function localDayString(now: Date): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function timeFmt(timeZone: string): Intl.DateTimeFormat {
  // hour: 'numeric' (not '2-digit') keeps the rendering platform-consistent:
  // ICU versions differ on whether 2-digit hours zero-pad ("09:00 AM" vs
  // "9:00 AM"), while numeric + 2-digit minutes is stable across them.
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * "2026-08-17T16:00:00.000Z" → "10:00 AM" in the given zone. Unknown zones fall
 * back to the device's own rendering of the instant (never throws).
 */
export function formatClockTime(iso: string, timeZone: string): string {
  try {
    return timeFmt(timeZone).format(new Date(iso));
  } catch {
    return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }
}

/**
 * A short zone label ("CT", "MT", "UTC", …) for a visible timezone indicator.
 * Falls back to the raw IANA name (and finally to the device's own zone label)
 * when the zone is unknown.
 */
export function timeZoneShortLabel(timeZone: string, when: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'short',
    }).formatToParts(when);
    const name = parts.find((p) => p.type === 'timeZoneName');
    return name ? name.value : timeZone;
  } catch {
    return timeZone;
  }
}

/**
 * A complete shift window in one zone: "10:00 AM – 6:00 PM (MT)". The zone
 * indicator is always present so a cross-zone reader can see WHY the numbers
 * differ from their own clock.
 */
export function formatShiftWindow(
  startIso: string,
  endIso: string,
  timeZone: string,
  when: Date = new Date(),
): string {
  return `${formatClockTime(startIso, timeZone)} \u2013 ${formatClockTime(
    endIso,
    timeZone,
  )} (${timeZoneShortLabel(timeZone, when)})`;
}

/**
 * A bare Postgres `date` as the API sends it: "1991-02-03". A calendar day,
 * with no instant and no zone attached to it.
 */
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a value that may be a calendar day OR a timestamp, without the
 * off-by-one that catches every date-only column.
 *
 * `new Date('1991-02-03')` is specified to parse as UTC midnight. Render that
 * with `toLocaleDateString` anywhere west of UTC and it prints 2 February — the
 * day before the one stored. `new Date('1991-02-03T06:00:00Z')` does not have
 * the problem, because it names a real instant.
 *
 * This mattered the moment the API started returning true days. Before that,
 * `date` columns came back as JS Dates at server-local midnight and shipped as
 * "1991-02-03T06:00:00.000Z" — so the admin rendered them correctly ONLY
 * because two bugs cancelled: a wrong value formatted by a wrong parse. Fixing
 * either one alone breaks the display, which is why the API change and this one
 * belong in the same commit.
 *
 * A day is therefore built from LOCAL components, so it means the same calendar
 * day to every viewer. Anything else is passed through to `new Date` unchanged.
 */
export function parseDayOrInstant(value: string): Date {
  const day = CALENDAR_DAY.exec(value);
  if (!day) return new Date(value);
  return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
}

/**
 * A CALENDAR DAY IN AN EXPLICIT ZONE — the frontend's `org_local_date()`.
 *
 * The SQL side of this product has had `org_local_date()` since the timesheet
 * week was cut on the organization's calendar rather than on UTC. The frontend
 * had `formatClockTime` for the hour and nothing for the DAY, so every screen
 * that needed one reached for `new Date(iso).toLocaleDateString()` — which is
 * the DEVICE's calendar. For an organization in `America/Chicago` read by
 * somebody in London, an interval that begins at 18:00 local on the 7th is
 * rendered as the 8th; the reader is looking at a different day than the one on
 * file, and nothing on the screen says so.
 *
 * Intl does the zone conversion, so this is correct across a DST boundary
 * WITHOUT any arithmetic of its own — which is the point. Adding or subtracting
 * 86,400,000 ms to walk a day is wrong by an hour twice a year, and on the two
 * boundary days it lands on the wrong date.
 *
 * Unknown zones fall back to the device's rendering rather than throwing; the
 * caller is responsible for saying WHOSE calendar it ended up being, which is
 * what `zoneNote` in `features/time/timeoffReview.ts` exists for.
 */
export function formatDayInZone(iso: string, timeZone: string): string {
  const at = new Date(iso);
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(at);
  } catch {
    return at.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

/**
 * THE LAST DAY OF A HALF-OPEN WINDOW, in the given zone.
 *
 * `time_off_request` stores `[starts_at, ends_at)` and `ends_at` is EXCLUSIVE:
 * three days off from the 7th is stored as ending at the first instant of the
 * 10th. Rendering `ends_at` verbatim shows a reviewer "Mar 7 – Mar 10" for a
 * request whose author asked for the 7th, 8th and 9th — a day the schedule
 * still shows them working, presented to the person deciding it.
 *
 * One millisecond back from the exclusive end is the last INSTANT inside the
 * window; formatting that instant in the zone yields the last local day. This
 * is instant arithmetic, not calendar arithmetic, so it is exactly right across
 * a DST boundary — the zone conversion happens afterwards, in Intl.
 */
export function lastDayInZone(endsAtExclusive: string, timeZone: string): string {
  const end = new Date(endsAtExclusive);
  return formatDayInZone(new Date(end.getTime() - 1).toISOString(), timeZone);
}

/**
 * A leave window as a reviewer reads it: "Mar 7 – Mar 9, 2026 (CT)".
 *
 * A window that opens and closes on the same local day renders as one day, not
 * as a range with the same date twice. The zone label is ALWAYS present, so a
 * reader in a different zone can see why the numbers are not their own clock's.
 */
export function formatLeaveWindow(
  startsAt: string,
  endsAtExclusive: string,
  timeZone: string,
): string {
  const first = formatDayInZone(startsAt, timeZone);
  const last = lastDayInZone(endsAtExclusive, timeZone);
  const label = timeZoneShortLabel(timeZone, new Date(startsAt));
  return first === last ? `${first} (${label})` : `${first} – ${last} (${label})`;
}
